import { useEffect, useRef, useState } from "react";
import { avatarColor, initials, isHttpUrl, parsePunkId, punkImageUrls, shortName } from "./format.js";

function mediaStreamOf(stream) {
  if (!stream) return null;
  const raw = stream.stream || stream.mediaStream;
  if (raw && typeof raw.getVideoTracks === "function") return raw;
  try {
    if (typeof stream.getNativeMediaStream === "function") {
      const ms = stream.getNativeMediaStream();
      if (ms && typeof ms.getVideoTracks === "function") return ms;
    }
    if (typeof stream.getMediaStream === "function") {
      const ms = stream.getMediaStream();
      if (ms && typeof ms.getVideoTracks === "function") return ms;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function streamAttrs(stream) {
  return stream?.getAttributes?.() || {};
}

export function streamHasAudio(stream) {
  const a = streamAttrs(stream);
  if (a.type === "screen" || a.screen === true) return false;
  if (a.type === "audio") return true;
  if (typeof stream?.hasAudio === "function" && stream.hasAudio()) return true;
  const ms = mediaStreamOf(stream);
  return !!ms && ms.getAudioTracks().some((t) => t.readyState !== "ended");
}

export function streamHasVideo(stream) {
  const a = streamAttrs(stream);
  if (a.type === "screen" || a.screen || a.type === "video") return true;
  if (typeof stream?.hasVideo === "function" && stream.hasVideo()) return true;
  const ms = mediaStreamOf(stream);
  return !!ms && ms.getVideoTracks().length > 0;
}

export function MeetAvatar({ identity, size = 46 }) {
  const name = identity?.name || identity?.userid || identity?.userId || "?";
  const urls = [];
  if (isHttpUrl(identity?.avatarUrl)) urls.push(identity.avatarUrl.trim());
  urls.push(...punkImageUrls(identity?.punkId));
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
  }, [urls.join("|")]);
  const src = idx < urls.length ? urls[idx] : null;
  if (src) {
    return (
      <img
        src={src}
        alt=""
        width={size}
        height={size}
        onError={() => setIdx((n) => n + 1)}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
      />
    );
  }
  return (
    <div
      className="bm-av"
      style={{
        width: size,
        height: size,
        marginLeft: 0,
        border: "none",
        fontSize: Math.max(11, size * 0.36),
        background: avatarColor(identity?.userid || identity?.userId || name),
      }}
    >
      {initials(name)}
    </div>
  );
}

function stripScreenSuffix(name) {
  return String(name || "").replace(/\s*[·•]\s*screen$/i, "").trim();
}

export function identityFromStream(stream, localIdentity, isLocal) {
  if (isLocal && localIdentity) return localIdentity;
  const a = streamAttrs(stream);
  const rawAvatar = a.avatarUrl || a.avatar || "";
  const avatarUrl = isHttpUrl(rawAvatar) ? rawAvatar.trim() : "";
  const punkId = parsePunkId(a.punkId) ?? (avatarUrl ? null : parsePunkId(rawAvatar));
  const name = stripScreenSuffix(a.actualName || a.nickname || a.name) || "Participant";
  return {
    userid: a.userid || a.userId || a.id || "",
    userId: a.userid || a.userId || a.id || "",
    name,
    avatarUrl,
    punkId,
    address: a.address || "",
  };
}

export function speakerKey(stream, isLocal, localUserId) {
  if (isLocal) return "local";
  const a = streamAttrs(stream);
  const uid = String(a.userid || a.userId || "").trim();
  if (localUserId && uid && uid === String(localUserId)) return "local";
  if (uid) return `u:${uid}`;
  const aid = String(a.id || "").trim();
  if (aid && aid !== String(stream?.getID?.() || "")) {
    if (localUserId && aid === String(localUserId)) return "local";
    return `u:${aid}`;
  }
  const name = stripScreenSuffix(a.actualName || a.nickname || a.name).toLowerCase();
  if (name && name !== "participant") return `n:${name}`;
  return `s:${stream?.getID?.()}`;
}

export function VideoTile({ stream, local, localIdentity, variant, person, micOff, audioStream }) {
  const ref = useRef(null);
  const video = streamHasVideo(stream);
  const ident = person || identityFromStream(stream, localIdentity, local);
  const screen = !!streamAttrs(stream).screen || streamAttrs(stream).type === "screen";
  const label = local
    ? (screen ? "You · screen" : "You")
    : screen
      ? `${shortName(ident.name)} · screen`
      : shortName(ident.name);

  useEffect(() => {
    const el = ref.current;
    if (!el || !video) return undefined;
    let stop = false;
    const attach = () => {
      if (stop) return true;
      const ms = mediaStreamOf(stream);
      const tracks = ms?.getVideoTracks?.() || [];
      if (!ms || tracks.length === 0) return false;
      if (el.srcObject !== ms) el.srcObject = ms;
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => undefined);
      return tracks.some((t) => t.readyState !== "ended");
    };
    const ms = mediaStreamOf(stream);
    ms?.addEventListener?.("addtrack", attach);
    const attached = attach();
    const t = attached ? null : setInterval(() => {
      if (attach()) clearInterval(t);
    }, 250);
    const onSub = () => attach();
    stream.addEventListener?.("added-stream", onSub);
    return () => {
      stop = true;
      clearInterval(t);
      ms?.removeEventListener?.("addtrack", attach);
      stream.removeEventListener?.("added-stream", onSub);
    };
  }, [stream, video, variant]);

  const meterSrc = screen ? null : audioStream || (streamHasAudio(stream) ? stream : null);
  const meter = meterSrc ? <MicMeter stream={meterSrc} muted={!!micOff} /> : null;
  const muteChip = micOff && !screen ? (
    <span className={`bm-mutechip ${variant === "stage" ? "stage" : ""}`} title="Microphone off" aria-label="Microphone off">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="1.8" />
        <path d="M6 11a6 6 0 0 0 12 0M12 17v3M8 21h8M5 5l14 14" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    </span>
  ) : null;

  if (!video) {
    return (
      <div className={`tile-fallback tile-${variant}`} style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6 }}>
        {muteChip}
        <MeetAvatar identity={ident} size={variant === "stage" ? 64 : 34} />
        {meter}
        <span style={{ fontSize: 12, color: "oklch(0.82 0.01 265)" }}>{label}</span>
      </div>
    );
  }

  return (
    <>
      <video ref={ref} className={`bm-video ${variant === "stage" ? "stage" : ""}`} autoPlay playsInline muted={local} data-stream-id={stream.getID?.()} />
      {variant !== "pip" ? <span className={variant === "stage" ? "bm-badge" : undefined} style={variant === "thumb" ? { position: "absolute", left: 8, bottom: 7, fontSize: 11 } : undefined}>{label}</span> : null}
      {meter}
      {muteChip}
    </>
  );
}

function MicMeter({ stream, muted }) {
  const bar = useRef(null);
  useEffect(() => {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return undefined;
    let stop = false;
    let raf = 0;
    let ctx;
    let src;
    let clone;
    let poll;
    const data = new Uint8Array(1024);
    const detach = () => {
      cancelAnimationFrame(raf);
      raf = 0;
      try {
        src?.disconnect();
      } catch {
        /* ignore */
      }
      try {
        clone?.stop();
      } catch {
        /* ignore */
      }
      ctx?.close?.().catch(() => undefined);
      src = null;
      clone = null;
      ctx = null;
    };
    const attach = () => {
      if (stop) return true;
      const ms = mediaStreamOf(stream);
      const track = (ms?.getAudioTracks?.() || []).find((t) => t.readyState !== "ended");
      if (!ms || !track) return false;
      if (src && (clone ? clone.readyState !== "ended" : track.readyState !== "ended")) return true;
      detach();
      ctx = new AC();
      try {
        clone = track.clone();
        src = ctx.createMediaStreamSource(new MediaStream([clone]));
      } catch {
        clone = null;
        try {
          src = ctx.createMediaStreamSource(ms);
        } catch {
          ctx.close().catch(() => undefined);
          ctx = null;
          return false;
        }
      }
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      src.connect(analyser);
      const tick = () => {
        if (ctx.state === "suspended") ctx.resume().catch(() => undefined);
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i += 1) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const speaking = !muted && track.enabled && track.readyState === "live";
        const level = speaking ? Math.min(1, Math.sqrt(sum / data.length) * 4) : 0;
        if (bar.current) bar.current.style.transform = `scaleX(${level})`;
        raf = requestAnimationFrame(tick);
      };
      tick();
      return true;
    };
    if (!attach()) {
      poll = setInterval(() => {
        if (attach() && poll) {
          clearInterval(poll);
          poll = 0;
        }
      }, 250);
    }
    const onSub = () => attach();
    stream.addEventListener?.("added-stream", onSub);
    return () => {
      stop = true;
      if (poll) clearInterval(poll);
      stream.removeEventListener?.("added-stream", onSub);
      detach();
    };
  }, [stream, muted]);
  return (
    <div className="bm-micmeter" aria-hidden="true">
      <i ref={bar} />
    </div>
  );
}
